"use client";
import gsap from "gsap";
import { useRouter } from "next/router";
import { useGSAP } from "@gsap/react";
import { AnimatePresence } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import ReactLenis, { LenisRef } from "lenis/react";
import className from "classnames/bind";
import {
  InducteeLandingHeader,
  InducteesAToZ,
  InducteesByClass,
} from "@components";
import {
  GetAllInducteesQuery,
  GetInducteeLandingQuery,
} from "@/__generated__/graphql";
import { useHeader } from "@/providers/HeaderContext";
import { useViewSize } from "@/providers/ViewSizeContext";
import { useClientMount } from "@/providers/ClientMountProvider";

import styles from "./InducteesLandingWrapper.module.scss";

const cx = className.bind(styles);

interface InducteesLandingWrapperProps {
  inducteeLandingData: GetInducteeLandingQuery["rhPage"]["inducteeLanding"];
  inductees: GetAllInducteesQuery["allInductees"];
}

export default function InducteesLandingWrapper({
  inducteeLandingData,
  inductees,
}: InducteesLandingWrapperProps) {
  // ========================================
  // SCROLL BEHAVIOR TOGGLE
  // ========================================
  // Controls how the page scrolls to top when switching between tabs
  //
  // USE_ANIMATED_SCROLL = true:
  //   - Smooth animated scroll using Lenis (0.8s duration)
  //   - More polished, professional feel
  //   - Better for user experience
  //
  // USE_ANIMATED_SCROLL = false:
  //   - Instant jump to top
  //   - Faster, more direct
  //   - Better for debugging/testing
  //
  // Change the value below to toggle:
  const USE_ANIMATED_SCROLL = true;
  // ========================================

  const {
    headline,
    searchPlaceholder,
    aZLabel,
    byClassLabel,
    noResultsTitle,
    noResultsBody,
    primaryCardCta,
    noResultsCta,
  } = inducteeLandingData;
  const router = useRouter();
  const lenisRef = useRef<LenisRef>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const landingEl = useRef<HTMLDivElement>(null);
  const defaultTab = router.asPath.includes("/classes/") ? "by-class" : "a-z";
  const [activeTab, setActiveTab] = useState<"a-z" | "by-class">(defaultTab);
  const [activeYear, setActiveYear] = useState<string>(null);
  const transitionOverlayRef = useRef<HTMLDivElement>(null);
  const { currentLanguage } = useHeader();
  const { isReady } = useViewSize();
  const { mounted } = useClientMount();

  // Helper function to scroll to top (animated or instant based on toggle)
  const scrollToTop = useCallback(() => {
    // Guard against SSR - only run on client
    if (typeof window === 'undefined') return;

    const lenis = lenisRef.current?.lenis;

    if (USE_ANIMATED_SCROLL) {
      // ANIMATED SCROLL - Smooth transition using Lenis
      if (lenis) {
        // Ensure Lenis is running before scrolling
        lenis.start();

        // Small delay to ensure Lenis is active and tab transition has started
        requestAnimationFrame(() => {
          lenis.scrollTo(0, {
            duration: 0.6,
            easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // easeOutExpo
            onComplete: () => {
              ScrollTrigger.refresh(true);
            }
          });
        });
      } else {
        // Fallback to GSAP if Lenis not available
        gsap.to(window, {
          scrollTo: { y: 0, autoKill: false },
          duration: 0.6,
          ease: "power2.out",
          onComplete: () => {
            ScrollTrigger.refresh(true);
          }
        });
      }
    } else {
      // INSTANT SCROLL - Immediate jump to top
      if (lenis) {
        lenis.scrollTo(0, { immediate: true });
      } else {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
      // Refresh ScrollTrigger after instant scroll
      requestAnimationFrame(() => {
        ScrollTrigger.refresh(true);
      });
    }
  }, [USE_ANIMATED_SCROLL]);

  useEffect(() => {
    ScrollTrigger.clearScrollMemory("manual");
    if ("scrollRestoration" in window?.history) {
      window.history.scrollRestoration = "manual";
    }

    return () => {
      if ("scrollRestoration" in window?.history) {
        window.history.scrollRestoration = "auto";
      }
    };
  }, []);

  useEffect(() => {
    const isByClass = router.asPath.includes("/classes/");
    const tab = isByClass ? "by-class" : "a-z";
    if (tab !== activeTab) {
      setActiveTab(tab);

      // Scroll to top when tab changes (e.g., browser back button)
      scrollToTop();
    }
  }, [router.asPath, activeTab, scrollToTop]);

  const onTabClick = (tab: "a-z" | "by-class") => {
    let baseRoute = "";
    if (currentLanguage !== "en") baseRoute = `/${currentLanguage}`;

    if (activeYear) {
      handleSelectYear();
      setActiveYear(null);
    }

    // Fade in black overlay to cover everything
    if (transitionOverlayRef.current) {
      gsap.to(transitionOverlayRef.current, {
        opacity: 1,
        duration: 0.3,
        ease: "power2.inOut",
        onComplete: () => {
          // Overlay is fully covering - now update tab and scroll
          setActiveTab(tab);
          scrollToTop();

          // Update route
          if (tab === "by-class") {
            router.push(`${baseRoute}/inductees/classes/`, undefined, {
              shallow: true,
              scroll: false,
            });
          } else {
            router.push(`${baseRoute}/inductees/`, undefined, {
              shallow: true,
              scroll: false,
            });
          }

          // After scroll completes + extra beat, fade out overlay to reveal new view
          gsap.delayedCall(0.9, () => { // 0.6s scroll + 0.3s buffer for view to settle
            if (transitionOverlayRef.current) {
              gsap.to(transitionOverlayRef.current, {
                opacity: 0,
                duration: 0.4,
                ease: "power2.inOut"
              });
            }
          });
        }
      });
    }
  };

  const handleSelectYear = useCallback(
    (year?: string) => {
      setActiveYear(year);
      gsap.set(headerRef.current.parentNode.parentNode, {
        pointerEvents: year ? "none" : "auto",
      });

      if (!year) {
        gsap.set(headerRef.current, {
          opacity: 0,
        });
      }
      gsap.to(headerRef.current, {
        height: year ? "12rem" : "auto",
        ease: "power4.inOut",
        duration: year ? 0 : 0.5,
        opacity: year ? 0 : 1,
        delay: year ? 0 : 0.5,
        pointerEvents: year ? "none" : "auto",
      });

      gsap.to(landingEl.current.querySelector("#carousel-spacer"), {
        width: year ? 0 : `min(45vw, 700px)`,
        marginLeft: "-3rem",
        ease: "power4.inOut",
        duration: year ? 0.3 : 1,
      });
    },
    [landingEl, headerRef]
  );

  useEffect(() => {
    gsap.registerPlugin(useGSAP);
    gsap.registerPlugin(ScrollTrigger);
    gsap.registerPlugin(ScrollToPlugin);

    function update(time: number) {
      lenisRef.current?.lenis?.raf(time * 1000);
      ScrollTrigger.update();
    }

    gsap.ticker.add(update);
    gsap.ticker.lagSmoothing(0);
    return () => gsap.ticker.remove(update);
  }, []);

  useEffect(() => {
    // Only initialize Lenis after client is mounted and viewport is ready
    if (!mounted || !isReady) {
      return;
    }

    if (lenisRef.current?.lenis) {
      const lenis = lenisRef.current?.lenis;
      lenis.scrollTo(0, { immediate: true });
      lenis.stop();
    }
  }, [lenisRef, mounted, isReady]);

  // Start Lenis when component or activeTab changes
  useEffect(() => {
    if (lenisRef.current?.lenis && mounted && isReady) {
      lenisRef.current.lenis.start();
    }
  }, [activeTab, mounted, isReady]);

  useEffect(() => {
    if (landingEl.current) {
      gsap.to(landingEl.current, { opacity: 1, duration: 0.3 });
    }
  }, [landingEl]);

  return (
    <>
      <ReactLenis
        root
        options={{ autoRaf: false, allowNestedScroll: true }}
        ref={lenisRef}
      />
      <div className="inductees-landing" ref={landingEl} style={{ opacity: 0 }}>
        {inductees.length > 0 && (
          <>
            <InducteeLandingHeader
              headline={headline}
              aZLabel={aZLabel}
              byClassLabel={byClassLabel}
              activeTab={activeTab}
              onTabClick={onTabClick}
              ref={headerRef}
            />
            <AnimatePresence initial={false} mode="wait">
              {activeTab === "a-z" ? (
                <InducteesAToZ
                  inductees={inductees}
                  noResultsTitle={noResultsTitle}
                  noResultsBody={noResultsBody}
                  searchPlaceholder={searchPlaceholder}
                  noResultsCta={noResultsCta}
                  key="a-z"
                  onAnimateIn={() => {
                    lenisRef.current?.lenis?.start();
                  }}
                />
              ) : (
                <InducteesByClass
                  inductees={inductees}
                  primaryCardCta={primaryCardCta}
                  key="by-class"
                  onSelectYear={(year) => handleSelectYear(year)}
                />
              )}
            </AnimatePresence>
          </>
        )}

        {/* Transition overlay - covers content during tab switches */}
        <div
          ref={transitionOverlayRef}
          className={cx("fade-to-black")}
        />
      </div>
    </>
  );
}
